import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import { Bell, Sparkles, Home, Receipt, ArrowUpRight, CreditCard, User, Search, Moon, Sun, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";
import { Logo } from "./Logo";
import { BottomNav } from "./BottomNav";
import { CommandBar } from "./CommandBar";
import { Onboarding } from "./Onboarding";
import guardLogo from "../assets/account-guard-logo.png";

function ThemeToggle({ variant }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  const Icon = isDark ? Sun : Moon;
  if (variant === "sidebar") {
    return (
      <button data-testid="theme-toggle" onClick={toggle}
        className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors">
        <Icon size={19} className="text-primary" /> {isDark ? "Light mode" : "Dark mode"}
      </button>
    );
  }
  return (
    <button data-testid="theme-toggle" onClick={toggle} aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="grid place-items-center w-10 h-10 rounded-full bg-secondary text-primary transition-transform duration-200 active:scale-90">
      <Icon size={19} />
    </button>
  );
}

const NAV_ITEMS = [
  { to: "/app", icon: Home, label: "Home", end: true, id: "side-nav-home" },
  { to: "/app/activity", icon: Receipt, label: "Activity", id: "side-nav-activity" },
  { to: "/app/send", icon: ArrowUpRight, label: "Send", id: "side-nav-send" },
  { to: "/app/cards", icon: CreditCard, label: "Cards", id: "side-nav-cards" },
  { to: "/app/profile", icon: User, label: "Profile", id: "side-nav-profile" },
];

function Sidebar({ unread, nav }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isTeen = user?.family?.is_teen;
  const navItems = NAV_ITEMS.filter((i) => !(isTeen && i.id === "side-nav-cards"));
  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 sticky top-0 h-screen border-r border-border bg-card/40 px-4 py-6">
      <div className="px-2 mb-8"><Logo size={34} /></div>
      <button data-testid="cmdbar-trigger" onClick={() => window.dispatchEvent(new Event("open-command-bar"))}
        className="flex items-center gap-2 mb-4 mx-1 px-3 h-10 rounded-xl bg-secondary text-muted-foreground text-sm hover:text-foreground transition-colors">
        <Search size={16} /> <span className="flex-1 text-left">Search…</span>
        <kbd className="text-[10px] font-semibold bg-background border border-border px-1.5 py-0.5 rounded">⌘K</kbd>
      </button>
      <nav className="space-y-1 flex-1">
        {navItems.map(({ to, icon: Icon, label, end, id }) => {
          const active = end ? pathname === to : pathname.startsWith(to);
          return (
            <NavLink key={to} to={to} end={end} data-testid={id}
              className={`flex items-center gap-3 px-3 h-11 rounded-xl font-medium text-sm transition-colors ${active ? "bg-primary text-white" : "text-muted-foreground hover:bg-secondary"}`}>
              <Icon size={19} strokeWidth={active ? 2.5 : 2} /> {label}
            </NavLink>
          );
        })}
      </nav>
      <div className="space-y-1 border-t border-border pt-4">
        <ThemeToggle variant="sidebar" />
        <button data-testid="side-assistant-btn" onClick={() => nav("/app/assistant")}
          className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors">
          <Sparkles size={19} className="text-primary" /> Assistant
        </button>
        <button data-testid="side-guard-btn" onClick={() => nav("/app/badge")}
          className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors">
          <img src={guardLogo} alt="" className="w-5 h-5 object-contain" /> Jack Cash Plus
        </button>
        <button data-testid="side-notifications-btn" onClick={() => nav("/app/notifications")}
          className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors">
          <span className="relative"><Bell size={19} className="text-primary" />
            {unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-destructive text-white text-[9px] font-bold">{unread > 9 ? "9+" : unread}</span>}
          </span> Notifications
        </button>
      </div>
    </aside>
  );
}

export function AppShell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    let prev = null;
    const beep = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
        o.start(); o.stop(ctx.currentTime + 0.45);
      } catch {}
    };
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (!active) return;
        if (prev !== null && data.count > prev) {
          beep();
          if ("Notification" in window && Notification.permission === "granted") {
            try { new Notification("Jack Cash", { body: "You have a new alert. Tap to review.", icon: "/favicon.png" }); } catch {}
          }
        }
        prev = data.count;
        setUnread(data.count);
      } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    return () => { active = false; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen w-full bg-background lg:flex">
      <Sidebar unread={unread} nav={nav} />
      <div className="flex-1 min-w-0 flex justify-center">
        <div className="w-full max-w-md md:max-w-lg lg:max-w-3xl relative pb-32 lg:pb-12 bg-background min-h-screen">
          {/* Mobile / tablet header (desktop uses the sidebar) */}
          <header className="lg:hidden sticky top-0 z-30 glass border-b border-border px-5 py-3 flex items-center justify-between">
            <Logo size={32} />
            <div className="flex items-center gap-2">
              <button data-testid="guard-btn" onClick={() => nav("/app/badge")}
                className="grid place-items-center w-10 h-10 rounded-full bg-secondary overflow-hidden transition-transform duration-200 active:scale-90">
                <img src={guardLogo} alt="Jack Cash Plus" className="w-6 h-6 object-contain" />
              </button>
              <button data-testid="notifications-btn" onClick={() => nav("/app/notifications")}
                className="relative grid place-items-center w-10 h-10 rounded-full bg-secondary text-primary transition-transform duration-200 active:scale-90">
                <Bell size={19} />
                {unread > 0 && (
                  <span data-testid="unread-dot" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-destructive text-white text-[10px] font-bold">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            </div>
          </header>
          <main className="px-5 pt-5 lg:px-10 lg:pt-10">
            {user?.family?.is_teen && (
              <div data-testid="teen-banner" className="mb-4 flex items-center gap-2 rounded-2xl bg-primary/10 border border-primary/20 px-4 py-2.5 text-xs font-semibold text-primary">
                <ShieldCheck size={15} className="shrink-0" />
                Managed account{user.family.parent_name ? ` · overseen by ${user.family.parent_name}` : ""}
              </div>
            )}
            <Outlet />
          </main>
        </div>
      </div>
      <BottomNav />
      <CommandBar />
      <Onboarding />
    </div>
  );
}
